import React, { useEffect, useState } from 'react';
import { PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../services/api';

function fmt(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return Number(n).toLocaleString();
}

/**
 * Scraper V2 Categories tab — manage data categories and see row counts.
 */
export default function ScraperV2CategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/scraper-v2/categories');
      if (res.data.success) setCategories(res.data.categories || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await api.post('/scraper-v2/categories', { name: newName.trim() });
      if (res.data.success) {
        toast.success(`Category "${res.data.category.name}" created`);
        setNewName('');
        await load();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create category');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditName(cat.name);
  };

  const saveEdit = async (id) => {
    if (!editName.trim()) return;
    setBusyId(id);
    try {
      const res = await api.patch(`/scraper-v2/categories/${id}`, { name: editName.trim() });
      if (res.data.success) {
        toast.success(res.data.migrated
          ? `Renamed to "${res.data.name}" (data migrated)`
          : `Updated to "${res.data.name}"`);
        setEditingId(null);
        await load();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to rename');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (cat) => {
    const hasData = (cat.rawCount || 0) + (cat.filteredCount || 0) > 0;
    const msg = hasData
      ? `"${cat.name}" still has scraped data. Remove the category label anyway? (Rows keep their tag.)`
      : `Remove category "${cat.name}"?`;
    if (!window.confirm(msg)) return;
    setBusyId(cat.id);
    try {
      const res = await api.delete(`/scraper-v2/categories/${cat.id}`, {
        params: hasData ? { force: 'true' } : {},
      });
      if (res.data.success) {
        toast.success(`Removed "${cat.name}"`);
        await load();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
          Data categories
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Categories label source batches on a scrape session. Use them when adding source accounts
          and bind CTA tracking links per category in Models.
        </p>
        <form onSubmit={handleCreate} className="flex flex-wrap gap-2">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New category (e.g. Gaming)"
            className="flex-1 min-w-[12rem] text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
          />
          <button
            type="submit"
            disabled={creating || !newName.trim()}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-lg"
          >
            <PlusIcon className="w-4 h-4" />
            {creating ? 'Adding…' : 'Add category'}
          </button>
        </form>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800/80 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3 text-right">Sources</th>
              <th className="px-4 py-3 text-right">Raw</th>
              <th className="px-4 py-3 text-right">Filtered</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {loading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-500">Loading…</td>
              </tr>
            )}
            {!loading && categories.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-500">No categories yet.</td>
              </tr>
            )}
            {!loading && categories.map((cat) => (
              <tr key={cat.id} className="bg-white dark:bg-gray-900">
                <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                  {editingId === cat.id ? (
                    <input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="w-full max-w-xs text-sm px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                      autoFocus
                    />
                  ) : (
                    cat.name
                  )}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {fmt(cat.sourceCount)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {fmt(cat.rawCount)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-emerald-600 dark:text-emerald-400">
                  {fmt(cat.filteredCount)}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    {editingId === cat.id ? (
                      <>
                        <button
                          type="button"
                          disabled={busyId === cat.id}
                          onClick={() => saveEdit(cat.id)}
                          className="px-2 py-1 text-xs rounded bg-indigo-600 text-white"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingId(null)}
                          className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => startEdit(cat)}
                          className="p-1.5 rounded text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
                          title="Rename"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          disabled={busyId === cat.id}
                          onClick={() => handleDelete(cat)}
                          className="p-1.5 rounded text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
